import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { quotesApi, customersApi, catalogApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import QuoteMaterialsCPQ from '../components/QuoteMaterialsCPQ';
import QuoteServicesCPQ from '../components/QuoteServicesCPQ';
import { QuoteSimulationTab } from '../components/quotes/QuoteSimulationTab';
import { QuoteAddMaterialDialog } from '../components/quotes/QuoteAddMaterialDialog';
import { QuoteAddServiceDialog } from '../components/quotes/QuoteAddServiceDialog';
import { QuoteStatusDialog } from '../components/quotes/QuoteStatusDialog';
import { QuoteCustomerTab, PLANT_TYPES } from '../components/quotes/QuoteCustomerTab';
import {
    ArrowLeft,
    User,
    Sun,
    Wrench,
    FileText,
    Calculator,
    Pencil,
    CheckCircle,
    XCircle,
    Clock,
    PaperPlaneTilt,
    ArrowsClockwise,
    ArrowRight,
    Package,
    TrendUp,
    CurrencyEur,
    CaretDown,
    FilePdf,
} from '@phosphor-icons/react';

// Stati del preventivo
const QUOTE_STATUSES = {
    bozza: { label: 'Bozza', color: 'bg-zinc-100 text-zinc-700', icon: Pencil, editable: true },
    in_preparazione: { label: 'In Preparazione', color: 'bg-blue-100 text-blue-700', icon: Clock, editable: true },
    inviato: { label: 'Inviato', color: 'bg-purple-100 text-purple-700', icon: PaperPlaneTilt, editable: false },
    in_revisione: { label: 'In Revisione', color: 'bg-orange-100 text-orange-700', icon: ArrowsClockwise, editable: true },
    accettato: { label: 'Accettato', color: 'bg-green-100 text-green-700', icon: CheckCircle, editable: false },
    rifiutato: { label: 'Rifiutato', color: 'bg-red-100 text-red-700', icon: XCircle, editable: false },
    scaduto: { label: 'Scaduto', color: 'bg-zinc-100 text-zinc-500', icon: Clock, editable: false },
    convertito: { label: 'Convertito', color: 'bg-emerald-100 text-emerald-700', icon: ArrowRight, editable: false },
};

const LINE_TYPE_ICONS = {
    MATERIAL: Package,
    INSTALLATION_SERVICE: Wrench,
    BUREAUCRATIC_SERVICE: FileText,
    MANUAL: Pencil
};

export default function QuoteDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [serviceCatalog, setServiceCatalog] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    
    // Dialogs
    const [showAddMaterial, setShowAddMaterial] = useState(false);
    const [showAddService, setShowAddService] = useState(false);
    const [showStatusDialog, setShowStatusDialog] = useState(false);
    
    // Material selector state
    const [catalogItems, setCatalogItems] = useState([]);
    const [catalogSearch, setCatalogSearch] = useState('');
    const [catalogTypeFilter, setCatalogTypeFilter] = useState('all');
    const [catalogBrandFilter, setCatalogBrandFilter] = useState('all');
    const [catalogBrands, setCatalogBrands] = useState([]);
    const [loadingCatalog, setLoadingCatalog] = useState(false);
    
    // PDF Download
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    
    // Customer mode: 'existing' or 'new'
    const [customerMode, setCustomerMode] = useState('existing');
    const [newCustomerData, setNewCustomerData] = useState({
        tipo: 'privato',
        nome: '',
        cognome: '',
        ragione_sociale: '',
        codice_fiscale: '',
        partita_iva: '',
        email: '',
        telefono: '',
        indirizzo: '',
        citta: '',
        provincia: '',
        cap: ''
    });
    const [savingCustomer, setSavingCustomer] = useState(false);
    
    // Bill extraction state
    const [extractingBill, setExtractingBill] = useState(false);
    const [billFile, setBillFile] = useState(null);
    
    // Service catalog state
    const [selectedServiceCategory, setSelectedServiceCategory] = useState(null);

    const loadQuote = useCallback(async () => {
        try {
            setLoading(true);
            const response = await quotesApi.getById(id);
            setQuote(response.data);
        } catch (error) {
            console.error('Error loading quote:', error);
            toast.error('Errore nel caricamento del preventivo');
            navigate('/preventivi');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    const loadCustomers = async () => {
        try {
            const response = await customersApi.getAll({ limit: 200 });
            const data = response.data;
            setCustomers(Array.isArray(data) ? data : (data.items || []));
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    const loadServiceCatalog = async () => {
        try {
            const response = await quotesApi.getServiceCatalog({ active_only: true });
            setServiceCatalog(response.data.items || []);
        } catch (error) {
            console.error('Error loading service catalog:', error);
        }
    };

    const loadCatalogItems = useCallback(async () => {
        try {
            setLoadingCatalog(true);
            const params = { active_only: true, limit: 100 };
            if (catalogTypeFilter && catalogTypeFilter !== 'all') {
                params.type_codes = catalogTypeFilter;
            }
            if (catalogBrandFilter && catalogBrandFilter !== 'all') {
                params.brand = catalogBrandFilter;
            }
            if (catalogSearch) {
                params.search = catalogSearch;
            }
            
            const response = await catalogApi.getItems(params);
            setCatalogItems(response.data.items || []);
            
            // Load brands for filter
            const brandsRes = await catalogApi.getBrands(catalogTypeFilter !== 'all' ? { type_codes: catalogTypeFilter } : {});
            setCatalogBrands(brandsRes.data || []);
        } catch (error) {
            console.error('Error loading catalog:', error);
        } finally {
            setLoadingCatalog(false);
        }
    }, [catalogTypeFilter, catalogBrandFilter, catalogSearch]);

    useEffect(() => {
        loadQuote();
        loadCustomers();
        loadServiceCatalog();
    }, [loadQuote]);

    useEffect(() => {
        if (showAddMaterial) {
            loadCatalogItems();
        }
    }, [showAddMaterial, loadCatalogItems]);

    const isEditable = useMemo(() => {
        if (!quote) return false;
        return QUOTE_STATUSES[quote.status]?.editable ?? false;
    }, [quote]);

    const handleUpdateQuote = async (updates) => {
        try {
            setSaving(true);
            await quotesApi.update(id, updates);
            await loadQuote();
            toast.success('Preventivo aggiornato');
        } catch (error) {
            console.error('Error updating quote:', error);
            toast.error('Errore nell\'aggiornamento');
        } finally {
            setSaving(false);
        }
    };

    const handleAddFromCatalog = async (catalogItem) => {
        try {
            setSaving(true);
            await quotesApi.addFromCatalog(id, catalogItem.id, 1);
            await loadQuote();
            toast.success(`${catalogItem.brand} ${catalogItem.model} aggiunto`);
        } catch (error) {
            console.error('Error adding item:', error);
            toast.error('Errore nell\'aggiunta');
        } finally {
            setSaving(false);
        }
    };

    const handleAddService = async (service) => {
        try {
            setSaving(true);
            await quotesApi.addService(id, service.id, 1);
            await loadQuote();
            toast.success(`${service.name} aggiunto`);
        } catch (error) {
            console.error('Error adding service:', error);
            toast.error('Errore nell\'aggiunta');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateLines = async (newLines) => {
        try {
            setSaving(true);
            await quotesApi.updateLines(id, newLines);
            await loadQuote();
            toast.success('Righe aggiornate');
        } catch (error) {
            console.error('Error updating lines:', error);
            toast.error('Errore nell\'aggiornamento');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveLine = async (index) => {
        if (!quote?.lines) return;
        const newLines = quote.lines.filter((_, i) => i !== index);
        await handleUpdateLines(newLines);
    };

    const handleUpdateLineQuantity = async (index, quantity) => {
        if (!quote?.lines) return;
        const newLines = [...quote.lines];
        newLines[index] = { ...newLines[index], quantity: parseFloat(quantity) || 1 };
        await handleUpdateLines(newLines);
    };

    const handleUpdateLinePrice = async (index, price) => {
        if (!quote?.lines) return;
        const newLines = [...quote.lines];
        newLines[index] = { ...newLines[index], unit_price: parseFloat(price) || 0 };
        await handleUpdateLines(newLines);
    };

    const handleCalculateSimulation = async () => {
        try {
            setSaving(true);
            await quotesApi.calculateSimulation(id);
            await loadQuote();
            toast.success('Simulazione calcolata');
        } catch (error) {
            console.error('Error calculating simulation:', error);
            toast.error(error.response?.data?.detail || 'Errore nel calcolo');
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            setSaving(true);
            await quotesApi.updateStatus(id, newStatus);
            await loadQuote();
            setShowStatusDialog(false);
            toast.success(`Stato aggiornato a "${QUOTE_STATUSES[newStatus]?.label}"`);
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error(error.response?.data?.detail || 'Errore nel cambio stato');
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadPdf = async (version = 'detailed') => {
        try {
            setDownloadingPdf(true);
            const versionSuffix = version === 'lite' ? '_lite' : '';
            const filename = quote.revision > 1 
                ? `Preventivo_${quote.quote_number}_v${quote.revision}${versionSuffix}.pdf`
                : `Preventivo_${quote.quote_number}${versionSuffix}.pdf`;
            await quotesApi.downloadPdf(id, filename, version);
            toast.success(`PDF ${version === 'lite' ? 'semplificato' : 'dettagliato'} scaricato con successo`);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Errore nel download del PDF');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const handleSaveNewCustomer = async () => {
        // Validazione
        if (newCustomerData.tipo === 'privato') {
            if (!newCustomerData.nome || !newCustomerData.cognome) {
                toast.error('Nome e cognome sono obbligatori');
                return;
            }
        } else {
            if (!newCustomerData.ragione_sociale) {
                toast.error('Ragione sociale è obbligatoria');
                return;
            }
        }
        
        try {
            setSavingCustomer(true);
            // Crea nuovo cliente tramite API customers
            const customerPayload = {
                tipo: newCustomerData.tipo,
                nome: newCustomerData.nome,
                cognome: newCustomerData.cognome,
                ragione_sociale: newCustomerData.ragione_sociale,
                codice_fiscale: newCustomerData.codice_fiscale,
                partita_iva: newCustomerData.partita_iva,
                email: newCustomerData.email,
                telefono: newCustomerData.telefono,
                indirizzo: newCustomerData.indirizzo,
                citta: newCustomerData.citta,
                provincia: newCustomerData.provincia,
                cap: newCustomerData.cap
            };
            
            const response = await customersApi.create(customerPayload);
            const newCustomerId = response.data.id;
            
            // Associa al preventivo
            await handleUpdateQuote({ customer_id: newCustomerId });
            
            // Refresh customers list
            await loadCustomers();
            
            // Reset form e torna a modalità esistente
            setCustomerMode('existing');
            setNewCustomerData({
                tipo: 'privato',
                nome: '',
                cognome: '',
                ragione_sociale: '',
                codice_fiscale: '',
                partita_iva: '',
                email: '',
                telefono: '',
                indirizzo: '',
                citta: '',
                provincia: '',
                cap: ''
            });
            
            toast.success('Cliente creato e associato al preventivo');
        } catch (error) {
            console.error('Error creating customer:', error);
            toast.error(error.response?.data?.detail || 'Errore nella creazione del cliente');
        } finally {
            setSavingCustomer(false);
        }
    };

    const handleBillUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        // Verifica tipo file
        const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            toast.error('Formato non supportato. Usa JPG, PNG, WEBP o PDF.');
            return;
        }
        
        // Verifica dimensione (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File troppo grande. Massimo 10MB.');
            return;
        }
        
        setBillFile(file);
        setExtractingBill(true);
        
        try {
            // Converti in base64
            const reader = new FileReader();
            reader.onload = async () => {
                const base64 = reader.result.split(',')[1];
                // Usa il vero mime type - il backend gestirà la conversione PDF
                const mimeType = file.type;
                
                try {
                    const response = await quotesApi.extractBillData(id, base64, mimeType);
                    
                    if (response.data.success) {
                        const pages = response.data.pages_processed || 1;
                        toast.success(`Dati bolletta estratti con successo${pages > 1 ? ` (${pages} pagine analizzate)` : ''}!`);
                        await loadQuote(); // Ricarica preventivo con nuovi dati
                    } else {
                        toast.error(response.data.error || 'Errore nell\'estrazione');
                    }
                } catch (err) {
                    console.error('Bill extraction error:', err);
                    toast.error(err.response?.data?.detail || 'Errore nell\'estrazione dei dati dalla bolletta');
                } finally {
                    setExtractingBill(false);
                }
            };
            reader.readAsDataURL(file);
        } catch (error) {
            console.error('File read error:', error);
            toast.error('Errore nella lettura del file');
            setExtractingBill(false);
        }
    };

    const handleUpdateSupplyData = async (field, value) => {
        try {
            const updateData = { [field]: value };
            await quotesApi.updateSupplyData(id, updateData);
            await loadQuote();
        } catch (error) {
            console.error('Error updating supply data:', error);
            toast.error('Errore nell\'aggiornamento dei dati fornitura');
        }
    };

    // Calcola dati impianto automaticamente dai materiali
    const calculatedPlantData = useMemo(() => {
        if (!quote?.lines) return { potenza_kwp: 0, numero_moduli: 0, accumulo_kwh: 0, has_accumulo: false };
        
        let potenza_kwp = 0;
        let numero_moduli = 0;
        let accumulo_kwh = 0;
        let has_accumulo = false;
        
        quote.lines.forEach(line => {
            if (line.line_type !== 'MATERIAL') return;
            
            const specs = line.technical_snapshot?.technical_specs || {};
            
            // Moduli fotovoltaici - supporta sia 'pmax_w' (formato catalogo) che 'potenza_wp' (legacy)
            if (line.category === 'MODULI') {
                const qty = line.quantity || 0;
                const potenza_wp = specs.pmax_w || specs.potenza_wp || 0;
                numero_moduli += qty;
                potenza_kwp += (qty * potenza_wp) / 1000;
            }
            
            // Batterie/Storage - supporta vari formati
            if (line.category === 'STORAGE') {
                has_accumulo = true;
                const capacita = specs.capacita_nominale_kwh || specs.capacita_utilizzabile_kwh || specs.capacita_kwh || 0;
                accumulo_kwh += (line.quantity || 1) * capacita;
            }
        });
        
        return {
            potenza_kwp: Math.round(potenza_kwp * 100) / 100,
            numero_moduli,
            accumulo_kwh: Math.round(accumulo_kwh * 100) / 100,
            has_accumulo
        };
    }, [quote?.lines]);

    const formatCurrency = (value) => {
        if (!value && value !== 0) return '-';
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const getCustomerName = (customer) => {
        if (!customer) return '';
        if (customer.tipo === 'azienda' && customer.ragione_sociale) {
            return customer.ragione_sociale;
        }
        return `${customer.nome || ''} ${customer.cognome || ''}`.trim();
    };

    // CPQ: Aggiorna singola riga
    const handleUpdateLine = async (lineIndex, data) => {
        try {
            await quotesApi.updateLine(id, lineIndex, { line_index: lineIndex, ...data });
            await loadQuote();
        } catch (error) {
            console.error('Error updating line:', error);
            toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento della riga');
            throw error;
        }
    };

    // CPQ: Elimina singola riga
    const handleDeleteLine = async (lineIndex) => {
        try {
            await quotesApi.deleteLine(id, lineIndex);
            await loadQuote();
        } catch (error) {
            console.error('Error deleting line:', error);
            toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione della riga');
            throw error;
        }
    };

    // Group lines by type
    const linesByType = useMemo(() => {
        if (!quote?.lines) return { materials: [], installation: [], bureaucracy: [] };
        return {
            materials: quote.lines.filter(l => l.line_type === 'MATERIAL'),
            installation: quote.lines.filter(l => l.line_type === 'INSTALLATION_SERVICE'),
            bureaucracy: quote.lines.filter(l => l.line_type === 'BUREAUCRATIC_SERVICE'),
        };
    }, [quote?.lines]);

    // Group services by category
    const servicesByCategory = useMemo(() => {
        const grouped = {};
        serviceCatalog.forEach(s => {
            if (!grouped[s.category]) grouped[s.category] = [];
            grouped[s.category].push(s);
        });
        return grouped;
    }, [serviceCatalog]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!quote) return null;

    const status = QUOTE_STATUSES[quote.status] || QUOTE_STATUSES.bozza;
    const StatusIcon = status.icon;

    return (
        <div className="p-6 space-y-6" data-testid="quote-detail-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/preventivi')}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-zinc-900">{quote.quote_number}</h1>
                            {quote.revision > 1 && (
                                <Badge variant="outline">v{quote.revision}</Badge>
                            )}
                            <Badge className={`${status.color} gap-1`}>
                                <StatusIcon className="w-3 h-3" />
                                {status.label}
                            </Badge>
                        </div>
                        <p className="text-zinc-500 mt-1">
                            {quote.customer_name || 'Cliente non specificato'} • 
                            {quote.plant_data?.potenza_kwp ? ` ${quote.plant_data.potenza_kwp} kWp` : ''} 
                            {quote.plant_data?.accumulo_presente ? ` + ${quote.plant_data.capacita_accumulo_kwh || '?'} kWh` : ''}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {isEditable && (
                        <Button variant="outline" onClick={() => setShowStatusDialog(true)}>
                            Cambia Stato
                        </Button>
                    )}
                    <Button onClick={handleCalculateSimulation} disabled={saving}>
                        <Calculator className="w-4 h-4 mr-2" />
                        Calcola
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button 
                                disabled={downloadingPdf}
                                variant="default"
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="download-pdf-btn"
                            >
                                {downloadingPdf ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                ) : (
                                    <FilePdf className="w-4 h-4 mr-2" />
                                )}
                                Scarica PDF
                                <CaretDown className="w-4 h-4 ml-2" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem 
                                onClick={() => handleDownloadPdf('detailed')}
                                className="cursor-pointer"
                            >
                                <FilePdf className="w-4 h-4 mr-2" />
                                <div className="flex flex-col">
                                    <span className="font-medium">PDF Dettagliato</span>
                                    <span className="text-xs text-zinc-500">Tutti i prezzi e servizi</span>
                                </div>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                                onClick={() => handleDownloadPdf('lite')}
                                className="cursor-pointer"
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                <div className="flex flex-col">
                                    <span className="font-medium">PDF Semplificato</span>
                                    <span className="text-xs text-zinc-500">Solo totale finale</span>
                                </div>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Main Content */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="overview" className="gap-2">
                        <FileText className="w-4 h-4" />
                        <span className="hidden sm:inline">Riepilogo</span>
                    </TabsTrigger>
                    <TabsTrigger value="customer" className="gap-2">
                        <User className="w-4 h-4" />
                        <span className="hidden sm:inline">Cliente</span>
                    </TabsTrigger>
                    <TabsTrigger value="materials" className="gap-2">
                        <Package className="w-4 h-4" />
                        <span className="hidden sm:inline">Materiali</span>
                    </TabsTrigger>
                    <TabsTrigger value="services" className="gap-2">
                        <Wrench className="w-4 h-4" />
                        <span className="hidden sm:inline">Servizi</span>
                    </TabsTrigger>
                    <TabsTrigger value="simulation" className="gap-2">
                        <TrendUp className="w-4 h-4" />
                        <span className="hidden sm:inline">Simulazione</span>
                    </TabsTrigger>
                </TabsList>

                {/* OVERVIEW TAB */}
                <TabsContent value="overview" className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Economic Summary */}
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <CurrencyEur className="w-5 h-5" />
                                    Riepilogo Economico
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="p-4 bg-zinc-50 rounded-lg">
                                        <p className="text-sm text-zinc-500">Materiali</p>
                                        <p className="text-xl font-bold">{formatCurrency(quote.total_materials)}</p>
                                    </div>
                                    <div className="p-4 bg-zinc-50 rounded-lg">
                                        <p className="text-sm text-zinc-500">Installazione</p>
                                        <p className="text-xl font-bold">{formatCurrency(quote.total_installation)}</p>
                                    </div>
                                    <div className="p-4 bg-zinc-50 rounded-lg">
                                        <p className="text-sm text-zinc-500">Servizi</p>
                                        <p className="text-xl font-bold">{formatCurrency(quote.total_services)}</p>
                                    </div>
                                    <div className="p-4 bg-zinc-50 rounded-lg">
                                        <p className="text-sm text-zinc-500">Totale Netto</p>
                                        <p className="text-xl font-bold">{formatCurrency(quote.total_net)}</p>
                                    </div>
                                </div>
                                
                                <Separator />
                                
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-4">
                                            <span className="text-zinc-500">Sconto:</span>
                                            {isEditable ? (
                                                <Input
                                                    type="number"
                                                    className="w-24"
                                                    value={quote.discount_percent || 0}
                                                    onChange={(e) => handleUpdateQuote({ discount_percent: parseFloat(e.target.value) || 0 })}
                                                    disabled={saving}
                                                />
                                            ) : (
                                                <span>{quote.discount_percent || 0}%</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-zinc-500">IVA:</span>
                                            <span>{formatCurrency(quote.total_iva)}</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-zinc-500">TOTALE LORDO</p>
                                        <p className="text-3xl font-bold text-primary">{formatCurrency(quote.total_gross)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Plant Summary */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Sun className="w-5 h-5" />
                                    Dati Impianto
                                    <Badge variant="secondary" className="text-xs ml-auto">Auto</Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-zinc-500">Tipologia</span>
                                    <span className="font-medium">{PLANT_TYPES[quote.plant_data?.tipologia] || '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-zinc-500">Potenza</span>
                                    <span className="font-medium text-amber-600">
                                        {calculatedPlantData.potenza_kwp || quote.plant_data?.potenza_kwp || '-'} kWp
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-zinc-500">N. Moduli</span>
                                    <span className="font-medium text-amber-600">
                                        {calculatedPlantData.numero_moduli || quote.plant_data?.numero_moduli || '-'}
                                    </span>
                                </div>
                                {(calculatedPlantData.has_accumulo || quote.plant_data?.accumulo_presente) && (
                                    <div className="flex justify-between">
                                        <span className="text-zinc-500">Accumulo</span>
                                        <span className="font-medium text-amber-600">
                                            {calculatedPlantData.accumulo_kwh || quote.plant_data?.capacita_accumulo_kwh || '-'} kWh
                                        </span>
                                    </div>
                                )}
                                <Separator />
                                <div className="flex justify-between">
                                    <span className="text-zinc-500">Produzione</span>
                                    <span className="font-medium">{quote.simulation?.annual_production_kwh || '-'} kWh/anno</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Lines Summary */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Componenti dell'Offerta</CardTitle>
                            <CardDescription>{quote.lines?.length || 0} righe totali</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="divide-y">
                                {quote.lines?.map((line, index) => {
                                    const LineIcon = LINE_TYPE_ICONS[line.line_type] || Package;
                                    const lineTotal = (line.quantity || 1) * (line.unit_price || 0) * (1 - (line.discount_percent || 0) / 100);
                                    return (
                                        <div key={index} className="py-3 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <LineIcon className="w-4 h-4 text-zinc-400" />
                                                <div>
                                                    <p className="font-medium">{line.description}</p>
                                                    <p className="text-sm text-zinc-500">
                                                        {line.brand && `${line.brand} • `}
                                                        {line.quantity} {line.unit} × {formatCurrency(line.unit_price)}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="font-medium">{formatCurrency(lineTotal)}</span>
                                        </div>
                                    );
                                })}
                                {(!quote.lines || quote.lines.length === 0) && (
                                    <p className="text-center py-8 text-zinc-500">
                                        Nessun componente aggiunto. Vai alle sezioni Materiali o Servizi per aggiungere componenti.
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* CUSTOMER TAB */}
                <QuoteCustomerTab
                    quote={quote}
                    isEditable={isEditable}
                    customers={customers}
                    getCustomerName={getCustomerName}
                    customerMode={customerMode}
                    setCustomerMode={setCustomerMode}
                    newCustomerData={newCustomerData}
                    setNewCustomerData={setNewCustomerData}
                    savingCustomer={savingCustomer}
                    onSaveNewCustomer={handleSaveNewCustomer}
                    extractingBill={extractingBill}
                    onBillUpload={handleBillUpload}
                    onUpdateSupplyData={handleUpdateSupplyData}
                    onUpdateQuote={handleUpdateQuote}
                    calculatedPlantData={calculatedPlantData}
                />

                {/* MATERIALS TAB - CPQ */}
                <TabsContent value="materials" className="space-y-4">
                    <QuoteMaterialsCPQ
                        quote={quote}
                        isEditable={isEditable}
                        onUpdateLine={handleUpdateLine}
                        onDeleteLine={handleDeleteLine}
                        onOpenCatalog={() => setShowAddMaterial(true)}
                        userRole={user?.role || 'admin'}
                    />
                </TabsContent>

                {/* SERVICES TAB - CPQ */}
                <TabsContent value="services" className="space-y-4">
                    <QuoteServicesCPQ
                        quote={quote}
                        isEditable={isEditable}
                        onUpdateLine={handleUpdateLine}
                        onDeleteLine={handleDeleteLine}
                        onOpenServiceCatalog={(category) => {
                            setSelectedServiceCategory(category);
                            setShowAddService(true);
                        }}
                        userRole={user?.role || 'admin'}
                    />
                </TabsContent>

                {/* SIMULATION TAB */}
                <QuoteSimulationTab quote={quote} saving={saving} onCalculate={handleCalculateSimulation} />
            </Tabs>

            {/* Add Material Dialog */}
            <QuoteAddMaterialDialog
                open={showAddMaterial}
                onOpenChange={setShowAddMaterial}
                quote={quote}
                catalogSearch={catalogSearch}
                setCatalogSearch={setCatalogSearch}
                catalogTypeFilter={catalogTypeFilter}
                setCatalogTypeFilter={setCatalogTypeFilter}
                catalogBrandFilter={catalogBrandFilter}
                setCatalogBrandFilter={setCatalogBrandFilter}
                catalogBrands={catalogBrands}
                catalogItems={catalogItems}
                loadingCatalog={loadingCatalog}
                onAddItem={handleAddFromCatalog}
            />

            {/* Add Service Dialog */}
            <QuoteAddServiceDialog
                open={showAddService}
                onOpenChange={setShowAddService}
                selectedCategory={selectedServiceCategory}
                serviceCatalog={serviceCatalog}
                onAddService={handleAddService}
            />

            {/* Status Change Dialog */}
            <QuoteStatusDialog
                open={showStatusDialog}
                onOpenChange={setShowStatusDialog}
                quote={quote}
                status={status}
                onStatusChange={handleStatusChange}
            />
        </div>
    );
}
